import { useEffect, useRef, useState as useStateRaw, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { useLanguageContext } from '@/i18n/LanguageContext';
import Seo from '@/components/seo/Seo';
import HeroSection from './components/HeroSection';
import AcademyIdentity from './components/AcademyIdentity';
import ProgrammeOverview from './components/ProgrammeOverview';
import ClassFormat from './components/ClassFormat';
import OutcomeSection from './components/OutcomeSection';
import OverviewTable from './components/OverviewTable';
import FAQSection from './components/FAQSection';
import CTASection from './components/CTASection';
import RelatedPages from './components/RelatedPages';

function FadeInSection({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useStateRaw(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`fade-in-section ${isVisible ? 'is-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

export default function JapanButlerAcademyPage() {
  const { lang } = useLanguageContext();
  const { t } = useTranslation();

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: lang === 'en' ? 'Home' : 'ホーム',
        item: 'https://butler-academy.jp/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: lang === 'en' ? 'What is The Japan Butler Academy?' : '日本執事アカデミーとは',
        item: 'https://butler-academy.jp/japan-butler-academy',
      },
    ],
  };

  const orgStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    name: lang === 'en' ? 'The Japan Butler Academy' : '日本執事アカデミー',
    url: 'https://butler-academy.jp/japan-butler-academy',
    parentOrganization: {
      '@type': 'Organization',
      name: lang === 'en' ? 'The All Japan Butler Association' : '一般社団法人全日本執事協会',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '2-7-4 Aomi',
      addressLocality: lang === 'en' ? 'Koto-ku, Tokyo' : '東京都江東区',
      postalCode: '135-0064',
      addressCountry: 'JP',
    },
    telephone: '03-6824-4086',
    email: 'info@butler-academy.jp',
  };

  return (
    <>
      <Header />
      <main>
        <Seo route="/japan-butler-academy" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbStructuredData) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(orgStructuredData) }}
        />

        <HeroSection />

        <FadeInSection>
          <AcademyIdentity />
        </FadeInSection>

        <FadeInSection>
          <ProgrammeOverview />
        </FadeInSection>

        <FadeInSection>
          <ClassFormat />
        </FadeInSection>

        <FadeInSection>
          <OutcomeSection />
        </FadeInSection>

        <FadeInSection>
          <OverviewTable />
        </FadeInSection>

        <FadeInSection>
          <FAQSection />
        </FadeInSection>

        <FadeInSection>
          <CTASection />
        </FadeInSection>

        <FadeInSection>
          <RelatedPages />
        </FadeInSection>

        <div className="bg-background-50 py-8">
          {String(t('aboutAcademy.lastUpdated')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-center text-[13px] text-foreground-400 tracking-[0.05em]">{__s}</p>))}
        </div>

        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 w-11 h-11 rounded-full border border-accent-500/40 text-accent-500 hover:bg-accent-500/10 hover:border-accent-400 transition-all duration-300 flex items-center justify-center cursor-pointer z-40"
          aria-label={t('common.backToTop')}
        >
          <i className="ri-arrow-up-line"></i>
        </button>
      </main>
      <Footer />
    </>
  );
}
